/**
 * The Enterprise admin dashboard.
 *
 * ── What an admin sees, and what they deliberately do not ───────────────────
 * Storage allocation, workspaces, and the organisation's domain. Shape and
 * activity.
 *
 * Seats and single sign-on are not here. They belong to the organisation
 * itself, which lives in Mauvely Enterprise; this page is what Cloud knows
 * about one. Adding and removing members still works from here — those two
 * controls forward to that service, which is where the seat count is checked.
 *
 * They do **not** see file names, workspace names, or message contents, and
 * there is no button here that could reveal them. A workspace's name is
 * encrypted under its own key, wrapped to its members' public keys by their
 * browsers; an admin who is not a member holds no copy of that key and the
 * server holds none either. An admin who needs to read a team's files gets added
 * to that team, in the open, by someone already in it.
 *
 * That is worth stating on the page itself rather than only in a comment,
 * because the reasonable expectation of an "admin dashboard" is otherwise the
 * opposite — and because an escape hatch here would be a key Mauvely holds,
 * which would make the product's central claim false for exactly the customers
 * paying most for it.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, errorText } from '../api';
import { ds } from '../ds';
import { useApp } from '../state';
import { entitlements, GB_TO_MB } from '@shared/entitlements';
import { Footnote, PageTitle, Panel } from '../components/Shell';
import { SkeletonLines } from '../components/Skeleton';
import { accountOrigin } from './Vault';
import { formatBytes, formatWhen, monoPlain } from '../theme';
import type { OrgOverviewWire } from '../api';
import { useT } from '../i18n';

/** `pending`/`verified`/`failed` are the database's vocabulary, not a
 *  sentence — mapped once here rather than printed as the enum. */
const DOMAIN_STATE_LABEL: Record<string, string> = {
  pending: 'Pending',
  verified: 'Verified',
  failed: 'Not verified'
};

export function Admin() {
  const t = useT();
  const { Button, Icon, Input } = ds();
  const { me, config } = useApp();

  const [data, setData] = useState<OrgOverviewWire | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteGb, setInviteGb] = useState('50');
  const [busy, setBusy] = useState(false);

  const eligible = entitlements(me?.plan).organisation;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.org.overview();
      setData(res);
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not load your organisation.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ display: 'grid', gap: 20 }}>
        <PageTitle title={t('admin.administration')} meta="Enterprise" />
        <Panel style={{ padding: '26px 30px' }}>
          <SkeletonLines lines={4} label={t('admin.loading-your-organisation')} />
        </Panel>
      </div>
    );
  }

  if (!data?.organisation) {
    return (
      <div style={{ display: 'grid', gap: 24 }}>
        <PageTitle title={t('admin.administration')} meta="Part of Enterprise" />
        <Panel style={{ padding: '34px 30px', display: 'grid', gap: 16, maxWidth: 640 }}>
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              display: 'grid',
              placeItems: 'center',
              background: 'var(--accent-100)',
              color: 'var(--accent-600)'
            }}
          >
            <Icon name="building-2" size={22} />
          </span>
          <h2
            style={{
              fontFamily: "var(--font-display), 'Baloo 2', system-ui",
              fontWeight: 800,
              fontSize: 24,
              margin: 0,
              color: 'var(--text-strong)'
            }}
          >
            {eligible ? 'Your organisation is not set up yet.' : 'Run Cloud for your team.'}
          </h2>
          <span style={{ fontSize: 14.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
            {eligible
              ? 'Your plan includes an organisation, but one has not been provisioned. Get in touch and we will set it up.'
              : 'One storage pool across as many workspaces as you need, an audit log, and your own domain.'}
          </span>
          <div>
            <Button
              variant="primary"
              size="md"
              onClick={() => {
                window.location.href = eligible
                  ? 'mailto:sales@mauvely.com?subject=Organisation%20setup'
                  : `${accountOrigin()}/pricing`;
              }}
            >
              {eligible ? 'Contact us' : 'See Enterprise'}
            </Button>
          </div>
        </Panel>
      </div>
    );
  }

  const org = data.organisation;
  /** No ceiling: charged, never refused. -1 rather than a large number, so a
   *  client that has not been taught the sentinel shows something visibly wrong
   *  instead of a plausible wrong — which is what this page used to do. */
  const unmetered = org.storage.poolMb < 0;

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errorText(err, 'That did not work.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 22 }}>
      <PageTitle title={`${org.name}.`} meta="Enterprise" />

      {error && (
        <Panel style={{ padding: '14px 20px', background: 'var(--danger-100)' }}>
          <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</span>
        </Panel>
      )}

      {!data.admin && (
        <Panel style={{ padding: '16px 20px' }}>
          <span style={monoPlain}>
            {t('admin.you-are-a-member-of')}
          </span>
        </Panel>
      )}

      {/* ── storage pool ─────────────────────────────────────────────────── */}
      <Panel style={{ padding: '20px 24px', display: 'grid', gap: 12 }}>
        <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          {t('admin.storage-pool')}
        </span>
        {/* No ceiling means there is nothing for the allocations to be a share
            of. Formatting the sentinel gave "0 B allocated of -1048576 B · 0 B
            left to hand out", which is three wrong numbers in one sentence —
            and "0 B left" is the exact opposite of the truth. Same reasoning as
            the sidebar meter in components/Shell.tsx. */}
        {unmetered ? (
          <span style={monoPlain}>
            {formatBytes(org.storage.allocatedMb * 1024 * 1024)} allocated · no limit
          </span>
        ) : (
          <>
            <span style={monoPlain}>
              {formatBytes(org.storage.allocatedMb * 1024 * 1024)} allocated of{' '}
              {formatBytes(org.storage.poolMb * 1024 * 1024)} ·{' '}
              {formatBytes(org.storage.unallocatedMb * 1024 * 1024)} left to hand out
            </span>
            <div style={{ height: 6, borderRadius: 999, background: 'var(--surface-sunken)' }}>
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(100, Math.round((org.storage.allocatedMb / Math.max(1, org.storage.poolMb)) * 100))}%`,
                  borderRadius: 999,
                  background: 'var(--accent-600)'
                }}
              />
            </div>
          </>
        )}
        <Footnote>{t('admin.allocated-not-used')}</Footnote>
        {/*
          Where a member's share is changed. It is not here: until 2026-08-28 it
          was nowhere — `PATCH /api/cloud/org/members/:userId` accepted a
          storageMb and no screen ever sent one, so an admin could set a figure
          when adding somebody and never change it again.

          The control lives in the enterprise console, with seats, policy and
          the audit trail. The pool and the check against it stay here, because
          both are in this database.
        */}
        {config.enterpriseUrl && (
          <Footnote>
            <a
              href={`${config.enterpriseUrl}/usage`}
              style={{ color: 'var(--text-link)' }}
              rel="noreferrer"
            >
              {t('admin.change-a-members-share-in')}
            </a>
          </Footnote>
        )}
      </Panel>

      {/* ── members ──────────────────────────────────────────────────────── */}
      <Panel style={{ paddingTop: 6, paddingBottom: 6 }}>
        {data.members.length === 0 ? (
          <div style={{ padding: '30px 22px' }}>
            <span style={monoPlain}>{t('admin.nobody-has-been-added-yet')}</span>
          </div>
        ) : (
          data.members.map((m, i) => (
            <div
              key={m.userId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '12px 22px',
                borderBottom:
                  i === data.members.length - 1 ? 'none' : '1px solid var(--border-subtle)',
                flexWrap: 'wrap'
              }}
            >
              <span style={{ flex: 1, minWidth: 160, fontSize: 14 }}>
                {/* The name if there is one, then the address. An account that
                    has been deleted leaves its Cloud row behind and has
                    neither, and the id is what is honest to show. */}
                {m.name ?? m.email ?? <span style={monoPlain}>{m.userId}</span>}
                {m.role === 'admin' && (
                  <span
                    style={{
                      marginLeft: 8,
                      padding: '1px 7px',
                      borderRadius: 999,
                      fontSize: 11.5,
                      fontWeight: 700,
                      background: 'var(--accent-100)',
                      color: 'var(--accent-600)'
                    }}
                  >
                    admin
                  </span>
                )}
              </span>
              <span style={{ ...monoPlain, minWidth: 160 }}>
                {formatBytes(m.usedBytes)} of {formatBytes(m.limitMb * 1024 * 1024)}
              </span>
              {data.admin && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => void act(() => api.org.removeMember(m.userId))}
                >
                  Remove
                </Button>
              )}
            </div>
          ))
        )}
      </Panel>

      {data.admin && (
        <Panel style={{ padding: '20px 24px', display: 'grid', gap: 12 }}>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>
            {t('admin.add-a-member')}
          </span>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Input
                label={t('admin.their-email')}
                value={inviteEmail}
                onChange={(e) => setInviteEmail((e.target as HTMLInputElement).value)}
                placeholder={t('admin.someoneexamplecom')}
              />
            </div>
            <div style={{ width: 130 }}>
              <Input
                label={t('admin.storage-gb')}
                value={inviteGb}
                onChange={(e) => setInviteGb((e.target as HTMLInputElement).value)}
              />
            </div>
            <Button
              variant="primary"
              size="md"
              disabled={busy || !inviteEmail.trim()}
              onClick={() =>
                void act(async () => {
                  await api.org.addMember(inviteEmail.trim(), Number(inviteGb) * GB_TO_MB);
                  setInviteEmail('');
                })
              }
            >
              {t('admin.add')}
            </Button>
          </div>
          <Footnote>{t('admin.they-need-a-mauvely-account')}</Footnote>
        </Panel>
      )}

      {/* ── workspaces ───────────────────────────────────────────────────── */}
      {data.admin && (
        <Panel style={{ paddingTop: 6, paddingBottom: 6 }}>
          <div style={{ padding: '14px 22px 8px' }}>
            <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>
              {t('admin.workspaces')}
            </span>
          </div>
          {data.workspaces.length === 0 ? (
            <div style={{ padding: '10px 22px 26px' }}>
              <span style={monoPlain}>{t('admin.none-yet')}</span>
            </div>
          ) : (
            data.workspaces.map((w, i) => (
              <div
                key={w.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '11px 22px',
                  borderBottom:
                    i === data.workspaces.length - 1 ? 'none' : '1px solid var(--border-subtle)',
                  flexWrap: 'wrap'
                }}
              >
                {/* No name: it is encrypted under the workspace's own key and an
                    admin who is not a member holds no copy. Showing the id is
                    honest; showing a blank would look like a bug. */}
                <span style={{ ...monoPlain, flex: 1, minWidth: 150 }}>{w.id}</span>
                <span style={monoPlain}>{w.members} members</span>
                <span style={monoPlain}>{w.files} files</span>
                <span style={monoPlain}>{formatBytes(w.usedBytes)}</span>
                <span style={monoPlain}>{formatWhen(w.createdAt)}</span>
              </div>
            ))
          )}
          <div style={{ padding: '10px 22px 16px' }}>
            <Footnote>{t('admin.names-are-encrypted-to-each')}</Footnote>
          </div>
        </Panel>
      )}

      {/* ── domain ───────────────────────────────────────────────────────── */}
      {/*
        Read-only here. Claiming a hostname, proving it with a TXT record and
        watching the certificate come up are one screen in the enterprise
        console, shared with Learn's hostname — this page prints what that
        screen decided and links to it.
      */}
      {data.admin && (
        <Panel style={{ padding: '20px 24px', display: 'grid', gap: 12 }}>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>
            {t('admin.custom-domain')}
          </span>
          <span style={monoPlain}>
            {org.customDomain.hostname
              ? `${org.customDomain.hostname} · ${DOMAIN_STATE_LABEL[org.customDomain.state] ?? org.customDomain.state}`
              : 'None'}
          </span>
          {config.enterpriseUrl && (
            <div>
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  window.location.href = `${config.enterpriseUrl}/domains`;
                }}
              >
                {t('admin.manage-domains')}
              </Button>
            </div>
          )}
        </Panel>
      )}

    </div>
  );
}
